// HKDSE 2022 Paper 1 Knowledge Graph Samples - Part 4
// Most advanced questions from the complete 2022 paper

export const hkdse2022Samples_part4 = {
  polynomials_advanced: [
    {
      id: 'hkdse2022_polynomials_01',
      problem: "設 $p(x) = 2x^3 + ax^2 + bx - 20$，其中 $a$ 及 $b$ 均為常數。當 $p(x)$ 除以 $x^2 - 2x + 3$ 時，餘式為 $x + 13$。(a) 求 $a$ 及 $b$。(b) $x - 5$ 是否 $p(x)$ 的因式？試解釋你的答案。(c) 某人宣稱方程 $p(x) = 0$ 有兩個無理根。你是否同意？試解釋你的答案。",
      problemType: "polynomial_division_factors",
      difficulty: "harder",
      description: "Polynomial division and factorization",
      graphData: {
        nodes: [
          {
            id: "polynomial_structure",
            name: "Polynomial Structure",
            type: "concepts",
            description: "p(x) = 2x³ + ax² + bx - 20",
            value: "Cubic polynomial with unknown coefficients",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "division_remainder",
            name: "Division Remainder",
            type: "concepts",
            description: "Remainder when divided by x² - 2x + 3",
            value: "Remainder = x + 13",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "part_a_polynomial_division",
            name: "Part (a): Polynomial Division",
            type: "steps",
            description: "Use polynomial division to find a and b",
            value: "p(x) = (x² - 2x + 3)q(x) + (x + 13)",
            importance: 10,
            x: 150, y: -100, z: 20
          },
          {
            id: "part_a_solve_coefficients",
            name: "Part (a): Solve Coefficients",
            type: "steps",
            description: "Compare coefficients to find a and b",
            value: "a = -4, b = 7",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "part_b_factor_test",
            name: "Part (b): Factor Test",
            type: "steps",
            description: "Test if x - 5 is a factor",
            value: "p(5) = 0 → x - 5 is a factor",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_c_irrational_roots",
            name: "Part (c): Irrational Roots",
            type: "steps",
            description: "Analyze nature of roots",
            value: "Use discriminant or factor theorem",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_c_conclusion",
            name: "Part (c): Conclusion",
            type: "steps",
            description: "Determine if claim about irrational roots is correct",
            value: "Analyze root nature based on factorization",
            importance: 9,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) a = -4, b = 7 (b) Yes (c) Analysis of roots",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "polynomial_structure", to: "part_a_polynomial_division", type: "enables", strength: 9 },
          { from: "division_remainder", to: "part_a_polynomial_division", type: "enables", strength: 9 },
          { from: "part_a_polynomial_division", to: "part_a_solve_coefficients", type: "leads to", strength: 10 },
          { from: "part_a_solve_coefficients", to: "part_b_factor_test", type: "enables", strength: 8 },
          { from: "part_a_solve_coefficients", to: "part_c_irrational_roots", type: "enables", strength: 9 },
          { from: "part_c_irrational_roots", to: "part_c_conclusion", type: "leads to", strength: 9 },
          { from: "part_a_solve_coefficients", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_b_factor_test", to: "final_answers", type: "contributes to", strength: 8 },
          { from: "part_c_conclusion", to: "final_answers", type: "contributes to", strength: 9 }
        ]
      }
    }
  ],
  
  probability_advanced: [
    {
      id: 'hkdse2022_probability_01',
      problem: "某學校有 $120$ 名學生參加某項活動。該 $120$ 名學生中，$60$ 名是男生，$40$ 名是女生，其餘 $20$ 名是其他性別的學生。從該 $120$ 名學生中隨機選出 $3$ 名學生。(a) 求選出的 $3$ 名學生中至少有 $2$ 名是男生的概率。(b) 求選出的 $3$ 名學生中恰好有 $1$ 名是女生的概率。",
      problemType: "probability_combinations",
      difficulty: "intermediate",
      description: "Probability with combinations and multiple categories",
      graphData: {
        nodes: [
          {
            id: "student_distribution",
            name: "Student Distribution",
            type: "concepts",
            description: "Total 120 students: 60 male, 40 female, 20 other",
            value: "P(male) = 60/120 = 0.5, P(female) = 40/120 = 1/3",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "combination_formula",
            name: "Combination Formula",
            type: "concepts",
            description: "nCr = n!/(r!(n-r)!)",
            value: "Use combinations for probability calculations",
            importance: 8,
            x: -150, y: -100, z: 20
          },
          {
            id: "part_a_at_least_2_male",
            name: "Part (a): At Least 2 Male",
            type: "steps",
            description: "Calculate P(at least 2 male) = P(2 male) + P(3 male)",
            value: "Use combinations: C(60,2)×C(60,1) + C(60,3)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "part_a_calculation",
            name: "Part (a): Calculation",
            type: "steps",
            description: "Calculate the probability",
            value: "P(at least 2 male) = (1770×60 + 34220)/C(120,3)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "part_b_exactly_1_female",
            name: "Part (b): Exactly 1 Female",
            type: "steps",
            description: "Calculate P(exactly 1 female)",
            value: "C(40,1)×C(80,2)/C(120,3)",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_b_calculation",
            name: "Part (b): Calculation",
            type: "steps",
            description: "Calculate the probability",
            value: "P(exactly 1 female) = 40×3160/280840",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "Both probability values",
            value: "(a) Calculated probability (b) Calculated probability",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "student_distribution", to: "part_a_at_least_2_male", type: "enables", strength: 9 },
          { from: "combination_formula", to: "part_a_at_least_2_male", type: "enables", strength: 8 },
          { from: "part_a_at_least_2_male", to: "part_a_calculation", type: "leads to", strength: 9 },
          { from: "student_distribution", to: "part_b_exactly_1_female", type: "enables", strength: 8 },
          { from: "combination_formula", to: "part_b_exactly_1_female", type: "enables", strength: 8 },
          { from: "part_b_exactly_1_female", to: "part_b_calculation", type: "leads to", strength: 8 },
          { from: "part_a_calculation", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_b_calculation", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    }
  ],
  
  quadratic_advanced: [
    {
      id: 'hkdse2022_quadratic_01',
      problem: "設 $f(x) = x^2 + px + q$，其中 $p$ 及 $q$ 均為實常數。已知 $f(x) = 0$ 的兩個根為 $\\alpha$ 及 $\\beta$，其中 $\\alpha > \\beta$。若 $\\alpha - \\beta = 4$ 及 $\\alpha^2 + \\beta^2 = 34$，求 $p$ 及 $q$。",
      problemType: "quadratic_roots_relationships",
      difficulty: "intermediate",
      description: "Quadratic equations with root relationships",
      graphData: {
        nodes: [
          {
            id: "quadratic_function",
            name: "Quadratic Function",
            type: "concepts",
            description: "f(x) = x² + px + q with roots α and β",
            value: "α > β, α - β = 4, α² + β² = 34",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "vieta_formulas",
            name: "Vieta's Formulas",
            type: "concepts",
            description: "α + β = -p, αβ = q",
            value: "Sum and product of roots",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_find_sum",
            name: "Step 1: Find Sum of Roots",
            type: "steps",
            description: "Use α - β = 4 and α² + β² = 34",
            value: "(α + β)² = α² + β² + 2αβ = 34 + 2q",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_solve_system",
            name: "Step 2: Solve System",
            type: "steps",
            description: "Solve for α and β",
            value: "α + β = 6, α - β = 4 → α = 5, β = 1",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_find_coefficients",
            name: "Step 3: Find Coefficients",
            type: "steps",
            description: "Use Vieta's formulas to find p and q",
            value: "p = -(α + β) = -6, q = αβ = 5",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "Coefficients p and q",
            value: "p = -6, q = 5",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "quadratic_function", to: "step1_find_sum", type: "enables", strength: 9 },
          { from: "vieta_formulas", to: "step1_find_sum", type: "enables", strength: 9 },
          { from: "step1_find_sum", to: "step2_solve_system", type: "leads to", strength: 9 },
          { from: "step2_solve_system", to: "step3_find_coefficients", type: "leads to", strength: 9 },
          { from: "vieta_formulas", to: "step3_find_coefficients", type: "enables", strength: 9 },
          { from: "step3_find_coefficients", to: "final_answers", type: "leads to", strength: 10 }
        ]
      }
    }
  ]
}; 